import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import Icon from '~ComponentsImproved/Icon';
import linkIcon from '~/Assets/icons/link.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import PersonFilledIcon from '~Assets/icons/person-filled.svg';
import { Box } from '~Styles';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const BookingBlock = styled.div<{
  gridColumnStart: number;
  gridColumnSpan: number;
  offsetMinutes: number;
  isHighlighted?: boolean;
  isSelected?: boolean;
}>`
  position: absolute;
  left: ${(props) =>
    64 +
    (props.gridColumnStart - 2) * 61 +
    (props.offsetMinutes / 30) *
      61}px; /* 64px label + column position * (60px + 1px gap) + minute offset */
  width: ${(props) =>
    props.gridColumnSpan * 60 + (props.gridColumnSpan - 1)}px; /* span * column width + gaps */
  top: 4px;
  bottom: 4px;
  background: ${(props) =>
    props.isHighlighted
      ? 'linear-gradient(135deg, #667eea 0%, #9f7aea 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: 6px;
  padding: 2px 8px;
  color: var(--colorWhite);
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${({ isHighlighted, isSelected }) =>
    isHighlighted || isSelected
      ? '0 4px 16px rgba(102, 126, 234, 0.5)'
      : '0 2px 8px rgba(0, 0, 0, 0.3)'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: ${({ isHighlighted, isSelected }) => (isHighlighted || isSelected ? 20 : 5)};
  box-sizing: border-box;
  animation: ${({ isHighlighted }) => (isHighlighted ? fadeIn : 'none')} 0.3s ease-out;
  outline: ${({ isHighlighted, isSelected }) =>
    isHighlighted
      ? '2px solid var(--colorUIYellow)'
      : isSelected
      ? '2px solid var(--colorWhite)'
      : '2px solid transparent'};
  transition: outline 0.3s ease-out;
`;

const BookingName = styled(Heading)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface BookingBlockItemProps {
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  gridColumnStart: number;
  gridColumnSpan: number;
  offsetMinutes: number;
  onClick?: () => void;
  isHighlighted?: boolean;
  onHighlightComplete?: () => void;
  isSelected?: boolean;
}

const BookingBlockItem: React.FC<BookingBlockItemProps> = ({
  booking,
  gridColumnStart,
  gridColumnSpan,
  offsetMinutes,
  onClick,
  isHighlighted,
  onHighlightComplete,
  isSelected,
}) => {
  // Call onHighlightComplete after animation completes
  useEffect(() => {
    if (isHighlighted && onHighlightComplete) {
      const timer = setTimeout(() => {
        onHighlightComplete();
      }, 3000); // Clear highlight after 3 seconds

      return () => clearTimeout(timer);
    }
    // Return undefined when condition is not met
    return undefined;
  }, [isHighlighted, onHighlightComplete]);

  return (
    <BookingBlock
      gridColumnStart={gridColumnStart}
      gridColumnSpan={gridColumnSpan}
      offsetMinutes={offsetMinutes}
      onClick={onClick}
      isHighlighted={isHighlighted}
      isSelected={isSelected}
      data-booking-id={booking.id}>
      <Box display="flex" alignItems="center" gap="xs">
        {booking.tableIds.length > 1 && (
          <Icon src={linkIcon} height="16px" width="16px" color="var(--colorWhite)" />
        )}

        <BookingName level={6} color="var(--colorWhite)">
          {booking.guestName}
        </BookingName>

        <Box display="flex" alignItems="center" style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <Icon src={PersonFilledIcon} height="16px" width="16px" color="var(--colorWhite)" />
          <BookingName level={6} color="var(--colorWhite)">
            {booking.partySize}
          </BookingName>
        </Box>
      </Box>
    </BookingBlock>
  );
};

export default BookingBlockItem;
